/**
 * REST calls.
 *
 * Accounts and characters only. Once a socket is open, everything goes over that.
 */

import { apiUrl } from './paths';

export interface AccountSummary {
  accountId: string;
  username: string;
}

export interface CharacterSummary {
  id: string;
  name: string;
  combatLevel: number;
  totalLevel: number;
  lastSeenAt: string | null;
  /** What they look like, for the picture on their card. */
  look: { model: string; skin: number; eyes: number };
}

export class ApiError extends Error {
  constructor(
    readonly code: string,
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

const SESSION_KEY = 'inverse.session';

export function storedToken(): string | null {
  try {
    return window.localStorage.getItem(SESSION_KEY);
  } catch {
    // Discord's iframe can block storage. Losing the session on reload is a nuisance,
    // not a failure, so it is handled rather than thrown.
    return null;
  }
}

export function storeToken(token: string | null): void {
  try {
    if (token) window.localStorage.setItem(SESSION_KEY, token);
    else window.localStorage.removeItem(SESSION_KEY);
  } catch {
    // Nothing to do. The session lives in memory for this page load.
  }
}

async function request<T>(
  path: string,
  options: { method?: string; body?: unknown; token?: string | null } = {},
): Promise<T> {
  const response = await fetch(apiUrl(path), {
    method: options.method ?? 'GET',
    headers: {
      ...(options.body ? { 'content-type': 'application/json' } : {}),
      ...(options.token ? { authorization: `Bearer ${options.token}` } : {}),
    },
    body: options.body ? JSON.stringify(options.body) : undefined,
  });

  const text = await response.text();
  let payload: unknown = null;
  try {
    payload = text ? JSON.parse(text) : null;
  } catch {
    payload = null;
  }

  if (!response.ok) {
    const detail = payload as { error?: string; message?: string } | null;
    throw new ApiError(
      detail?.error ?? 'request_failed',
      detail?.message ?? friendlyError(detail?.error, response.status),
      response.status,
    );
  }

  return payload as T;
}

function friendlyError(code: string | undefined, status: number): string {
  switch (code) {
    case 'too_many_requests':
      return 'Too many attempts. Wait a minute and try again.';
    case 'name_taken':
      return 'That name is taken.';
    case 'name_invalid':
      return 'Names are 3 to 12 characters, letters and numbers.';
    case 'too_many_characters':
      return 'You already have three characters.';
    case 'password_wrong':
      return 'That password is not right.';
    case 'password_weak':
      return 'Passwords are at least eight characters.';
    case 'character_online':
      return 'That character is logged in. Log it out first.';
    case 'not_found':
      return 'That character is gone already.';
    case 'world_full':
      return 'The world is full. Try again shortly.';
    case 'world_updating':
      return 'The world is restarting. Try again in a moment.';
    case 'unauthorized':
      return 'Your session expired. Log in again.';
    default:
      return status >= 500 ? 'The server had a problem. Try again.' : 'That did not work.';
  }
}

export const api = {
  login: (username: string, password: string) =>
    request<{ token: string; account: AccountSummary }>('/api/auth/login', {
      method: 'POST',
      body: { provider: 'local', username, password },
    }),

  register: (username: string, password: string) =>
    request<{ token: string; account: AccountSummary }>('/api/auth/register', {
      method: 'POST',
      body: { provider: 'local', username, password },
    }),

  logout: (token: string) => request<{ ok: true }>('/api/auth/logout', { method: 'POST', token }),

  characters: (token: string) =>
    request<{ characters: CharacterSummary[] }>('/api/characters', { token }),

  createCharacter: (token: string, name: string, look: { skin: number; eyes: number }) =>
    request<{ character: { id: string; name: string } }>('/api/characters', {
      method: 'POST',
      body: { name, skin: look.skin, eyes: look.eyes },
      token,
    }),

  /** A model brought in from the tools. Refused for anybody without the rank. */
  uploadModel: (
    token: string,
    model: {
      name: string;
      title: string;
      contents: string;
      binary: boolean;
      tags: string[];
    },
  ) => request<{ ok: true }>('/api/models', { method: 'POST', body: model, token }),

  /** One of the boards: everybody by total level, by a skill, or by kills of a boss. */
  leaderboard: (token: string, kind: 'overall' | 'skill' | 'boss', of = 0) =>
    request<{ rows: { name: string; value: number; extra: number | null }[] }>(
      `/api/leaderboards?kind=${kind}&of=${of}`,
      { token },
    ),

  /** The bosses there are boards for. */
  bosses: (token: string) =>
    request<{ bosses: { id: number; name: string }[] }>('/api/leaderboards?kind=bosses', {
      token,
    }),

  /** A sound brought in from the tools, or brought in again under the same name to replace it. */
  uploadSound: (
    token: string,
    sound: { name: string; title: string; contents: string; kind: 'wav' | 'ogg' | 'mp3' },
  ) => request<{ ok: true }>('/api/sounds', { method: 'POST', body: sound, token }),

  play: (token: string, characterId: string) =>
    request<{ ticket: string }>('/api/play', { method: 'POST', body: { characterId }, token }),

  orderCharacters: (token: string, ids: readonly string[]) =>
    request<{ ok: true }>('/api/characters/order', { method: 'POST', body: { ids }, token }),

  /** Both of these ask for the password again: a session is not enough to undo an account. */
  deleteCharacter: (token: string, characterId: string, password: string) =>
    request<{ ok: true }>('/api/characters/delete', {
      method: 'POST',
      body: { characterId, password },
      token,
    }),

  resetCharacter: (token: string, characterId: string, password: string) =>
    request<{ ok: true }>('/api/characters/reset', {
      method: 'POST',
      body: { characterId, password },
      token,
    }),

  changePassword: (token: string, current: string, next: string) =>
    request<{ ok: true }>('/api/account/password', {
      method: 'POST',
      body: { current, next },
      token,
    }),
};
